import { computed, watch } from 'vue';
import { useAvailability, useTraits } from '@common-vue/media-card-large';
import { useType } from '@modules/types';
import { useColor } from '@modules/color';
import { useSize } from '@modules/size/composables';
import { useKillswitches, useProductFlags, useTopLevelCategoryFlags } from '@modules/featureControl';
import { useUpc } from '@modules/upc';

/**
 * @typedef {Object} UpcAvailability
 * @property {string} color
 * @property {string} size
 * @property {string} type
 * @property {boolean} available
 * @property {number} upcId
 */

/**
 *
 * @param {UpcAvailability} upc
 * @param {'color' | 'size' | 'type'} traitName
 * @param {traitId} traitId
 * @returns {boolean}
 */
const checkTrait = (upc, traitName, traitId = null) => (traitId === null) || (upc[traitName] === traitId.toString());

/**
 *
 * @param {UpcAvailability[]} availability
 * @param {{sizeId?: traitId, colorId?: traitId, typeId?: traitId}} ids
 * @returns {boolean}
 */
function isAnyUpcAvailable(availability, { sizeId, colorId, typeId }) {
  return availability.filter((upc) => checkTrait(upc, 'size', sizeId) && checkTrait(upc, 'color', colorId) && checkTrait(upc, 'type', typeId) && upc.available).length > 0;
}

function typeDrivenMapper(availability, types) {
  return types?.map((type) => ({ ...type, available: isAnyUpcAvailable(availability, { typeId: type.id }) })) || [];
}

function sizeDrivenMapper(availability, sizeProps) {
  return {
    ...sizeProps,
    sizes: sizeProps.sizes?.map((size) => ({ ...size, available: isAnyUpcAvailable(availability, { sizeId: size.id }) })) || [],
  };
}

function upcToMemberId(upcData, upcId) {
  return upcData.upcs[upcId]?.relationships?.selectedProduct;
}

/**
 *
 * @param {*} upcData
 * @param {UpcAvailability[]} availability
 * @param {"size" | "type"} name
 * @param {number} selectedTraitId
 */
function variantIdToMemberId(upcData, availability, name, selectedTraitId) {
  const upcId = availability.find((upc) => upc[name] === selectedTraitId.toString())?.upcId;
  return upcToMemberId(upcData, upcId);
}

export default function useConsolidated3rdVariant(productId, product) {
  const { killswitches: { disablePriceByColors } } = useKillswitches();
  const { selectedColorId } = useTraits(productId);
  const { colorProps } = useColor(productId);
  const { sizeProps, selectedSizeId } = useSize(productId, product);
  const {
    type: typeAvailability,
    size: sizeAvailability,
    color: colorAvailability,
  } = useAvailability(productId);

  const { traitsMap, upcData } = useUpc(productId);

  const { types, selectedTypeId } = useType(productId);

  const { isTypeDrivenThirdVariantFlag: isTypeDriven } = useProductFlags();

  const { isBeddingCategory } = useTopLevelCategoryFlags();

  const isSizeDriven = !isTypeDriven;

  const filteredSizeProps = computed(() => {
    if (isSizeDriven) {
      return sizeDrivenMapper(traitsMap.value, sizeProps.value);
    }

    const type = product.traits.types.typeMap[selectedTypeId.value] || {};
    const sizeIdsForType = Object.keys(type.sizeToColors).map((id) => parseInt(id, 10)) || [];

    return {
      ...sizeProps,
      sizes: sizeProps.value.sizes?.filter((size) => sizeIdsForType.includes(size.id)).map((size) => ({
        id: size.id, available: sizeAvailability.value[size.id], displayName: size.displayName, name: size.name,
      })),
    };
  });

  /**
   * @type {import('vue').ComputedRef<Type[]>}
   */
  const filteredTypes = computed(() => {
    if (isTypeDriven) {
      return typeDrivenMapper(traitsMap.value, types.value);
    }

    if (isBeddingCategory) {
      return types.value;
    }

    const size = product.traits.sizes.sizeMap[selectedSizeId.value ?? -1] || { typeToColors: {} };
    const typeIdsForSize = Object.keys(size.typeToColors).map((id) => parseInt(id, 10)) || [];

    return types.value.filter((type) => typeIdsForSize.includes(type.id));
  });

  const availableTypes = computed(() => filteredTypes.value.filter((type) => typeAvailability.value[type.id]));
  const availableSizes = computed(() => filteredSizeProps.value.sizes.filter((size) => sizeAvailability.value[size.id]));

  /**
   * @type {{type?: TypeId | null, size?: SizeId | null, color: ColorId | null}[]}
   */
  const selectedTraits = [];

  // Filter colors based on selected type, then mark availability
  const typeDrivenColorFiltering = () => {
    const type = product.traits.types.typeMap[selectedTypeId.value] || { colorToSizes: {} };

    // Get color IDs relevant to the selected type
    const colorIdsForType = Object.keys(type.colorToSizes).map((id) => parseInt(id, 10)) || [];
    const memberId = variantIdToMemberId(upcData.value, traitsMap.value, 'type', selectedTypeId.value);
    const priceToColors = disablePriceByColors ? [
      { colorIds: Object.keys(product.traits.colors?.colorMap || {}).map((key) => Number(key)) },
    ] : product.relationships.memberProductMap[memberId].traits?.traitsMaps.priceToColors;

    return {
      ...colorProps?.value,
      colors: colorProps?.value.colors
        .filter((color) => colorIdsForType.includes(color.id)) // Filter colors based on type
        .map((color) => ({
          ...color,
          available: colorAvailability.value[color.id], // mark availablity
        })),
      priceToColors,
    };
  };

  // Filter colors based on selected size, then mark availability
  const sizeDrivenColorFiltering = () => {
    const size = product.traits.sizes.sizeMap[selectedSizeId.value ?? -1] || { colorToTypes: {} };

    // Get color IDs relevant to the selected size
    const colorIdsForSize = Object.keys(size.colorToTypes).map((id) => parseInt(id, 10)) || [];
    const memberId = variantIdToMemberId(upcData.value, traitsMap.value, 'size', selectedSizeId.value);
    const priceToColors = disablePriceByColors ? [
      { colorIds: Object.keys(product.traits.colors?.colorMap || {}).map((key) => Number(key)) },
    ] : product.relationships.memberProductMap[memberId].traits?.traitsMaps.priceToColors;
    // console.log(memberIds);
    // console.log(colorIdsForSize);
    // console.log(colorProps.value.colors)
    // console.log(colorIdsForSize.map((colorId) => colorProps.value.colors.find(color => color.id === colorId)))

    return {
      ...colorProps?.value,
      colors: colorProps?.value.colors
        .filter((color) => colorIdsForSize.includes(color.id)) // Filter colors based on size
        .map((color) => ({
          ...color,
          available: colorAvailability.value[color.id], // mark availablity
        })),
      priceToColors,
    };
  };

  // Compute filtered colors based on the current selection flow
  const filteredColorProps = computed(() => (isTypeDriven ? typeDrivenColorFiltering() : sizeDrivenColorFiltering()));

  const availableColors = computed(() => {
    const { colors } = filteredColorProps.value;
    if (isSizeDriven) {
      return colors.filter((color) => isAnyUpcAvailable(traitsMap.value, { colorId: color.id, sizeId: selectedSizeId.value }));
    }
    return colors.filter((color) => isAnyUpcAvailable(traitsMap.value, { colorId: color.id, typeId: selectedTypeId.value }));
  });

  if (isSizeDriven) {
    watch(selectedSizeId, (newSize, oldSize) => {
      selectedTraits[oldSize] = {
        type: selectedTypeId.value,
        color: selectedColorId.value,
      };

      selectedTraits[newSize] = selectedTraits[newSize] || {};
      const newTraits = selectedTraits[newSize];

      newTraits.color = newTraits?.color ?? availableColors.value[0]?.id ?? null;
      selectedColorId.value = newTraits.color;

      newTraits.type = newTraits?.type ?? availableTypes.value[0]?.id ?? null;
      selectedTypeId.value = newTraits.type;
    });
  }

  if (isTypeDriven) {
    watch(selectedTypeId, (newType, oldType) => {
      selectedTraits[oldType] = {
        size: selectedSizeId.value,
        color: selectedColorId.value,
      };

      selectedTraits[newType] = selectedTraits[newType] || {};
      const newTraits = selectedTraits[newType];

      newTraits.color = newTraits?.color ?? availableColors.value[0]?.id ?? null;
      selectedColorId.value = newTraits.color;

      newTraits.size = newTraits?.size ?? availableSizes.value[0]?.id ?? null;
      selectedSizeId.value = newTraits.size;
    });
  }

  const firstUpcKeyForColorAndDrivenTrait = computed(() => {
    let selectedUpcKey;
    if (isSizeDriven) {
      selectedUpcKey = Object.keys(upcData.value.upcMap || {}).find((upcKey) => {
        const [upcColor, upcSize] = upcKey.split('_');
        return upcColor === selectedColorId.value?.toString() && upcSize === selectedSizeId.value?.toString();
      });
    } else {
      selectedUpcKey = Object.keys(upcData.value.upcMap || {}).find((upcKey) => {
        const [upcColor, , upcType] = upcKey.split('_');
        return upcColor === selectedColorId.value?.toString() && upcType === selectedTypeId.value?.toString();
      });
    }
    return selectedUpcKey ? upcData.value.upcMap[selectedUpcKey] : null;
  });

  return {
    sizeProps: filteredSizeProps,
    selectedSizeId,
    types: filteredTypes,
    selectedTypeId,
    isTypeDriven,
    sizeAvailability,
    colorProps: filteredColorProps,
    firstUpcKeyForColorAndDrivenTrait,
  };
}
